<i18n>
[
    "browse__total-results",
    "browse__clearFilters--title",
]
</i18n>

<template>
    <Layout :flushWithHeader="Boolean(!!contentfulTopper || showPageHeader)">
        <ProductTour
            v-if="shouldShowProductTour && isMounted"
            :tourName="productTourName"
            :productTourData="productTourData"
        />
        <PageHeader
            v-if="showPageHeader"
            :pageTitle="pageTitle || pageHeader?.editorialText?.headline"
            :pageSubTitle="pageSubTitle || pageHeader?.editorialText?.label"
            :backgroundImages="headerType ? {} : pageHeader?.backgroundImage"
            :textColor="pageHeader?.editorialText?.textColor"
            :headerType="headerType"
            :class="pageHeaderClass || defaultPageHeaderClass"
        />
        <slot name="custom-topper-content"></slot>
        <ContentfulContentBlock
            v-if="contentfulTopper"
            :contentRows="contentfulTopper"
            :contentTitle="contentfulTitle"
            :contentStartTime="contentfulStartTime"
            :contentEndTime="contentfulEndTime"
            class="u-spacer--2"
        />
        <CurationSubNav
            v-if="contentfulCurationNavItems"
            :contentfulCurationNavItems="contentfulCurationNavItems"
            :contentTitle="contentfulTitle"
            :hasEnteredViewport="true"
        />
        <CatalogGridContainer :catalogResponseId="clientResponseUuid">
            <CatalogActionBar
                v-if="enableFilterActionBar"
                :selectedFilterOptionsText="selectedFilterOptionsText"
                :selectedFilterOptionsCount="selectedFilterOptionsCount"
                :catalogContext="catalogContext"
                :sortOptions="sortOptions"
            />
            <CatalogResultsCount v-if="enableProductResultsCount">
                {{ totalProductResultText }} {{ t('browse__total-results', totalProductResults) }}
            </CatalogResultsCount>
            <template v-if="showEmptyCatalogContent">
                <CatalogEmptyFilteredContent
                    v-if="showFilterEmptyResults"
                    :contentfulContentBlockComponent="contentfulContentBlockComponent"
                    :getClearAllFiltersLinkObject="getClearAllFiltersLinkObject"
                    :emptyFilteredContentSlug="emptyFilteredContentSlug"
                />
                <template v-else-if="!showFilterEmptyResults && emptyCatalogContentfulContent">
                    <ContentfulContentBlock
                        :contentRows="emptyCatalogContentfulContent.rows"
                        :contentTitle="emptyCatalogContentfulContent.title"
                        class="u-spacer--2"
                    />
                    <slot name="additional-empty-content"></slot>
                </template>
            </template>
            <template v-else>
                <CatalogGrid
                    :products="products"
                    :class="gridBottomPadding"
                    :contentfulInGridContentRows="contentfulInGridContentRows"
                    :pagination="pagination"
                    catalogTileClasses="o-extra-small--6 o-large--3"
                    class="c-catalog-page__grid"
                    keyProperty="choiceId"
                >
                    <template #default="{ index, product, hasEnteredViewport }">
                        <CatalogTile
                            :analyticsType="analyticsType"
                            :closetListId="closetListId"
                            :productListId="productListId || categorySlug"
                            :productListName="productListName || catalogContext"
                            :hasEnteredViewport="isTileValidForProductTour(index) || hasEnteredViewport"
                            :displayAsCarousel="displayTilesAsCarousel"
                            :product="product"
                            :imagePreset="imagePreset"
                            :showAddToBox="showBoxCTAs"
                            :showInProductTour="isTileValidForProductTour(index)"
                            :catalogResponseId="catalogResponseId"
                            :useBottomMargin="false"
                            :isPropPrePurchase="isPrePurchase"
                            :selectedCuration="selectedCuration"
                            :faceOutImage="contentfulFaceOutImage"
                            :queryFilters="queryFilters"
                            @toggle-save-product-clicked="toggleProductClosetStatus({ ...$event, index })"
                        />
                    </template>
                    <template #inGridContent="{ contentfulInGridContent }">
                        <CatalogInGridRowContainer :eligibility="contentfulInGridContent.eligibility">
                            <CatalogInGridRow
                                :contentfulInGridContent="contentfulInGridContent"
                                :contentfulTitle="contentfulTitle"
                                :contentfulStartTime="contentfulStartTime"
                                :contentfulEndTime="contentfulEndTime"
                                :pagination="pagination"
                            />
                        </CatalogInGridRowContainer>
                    </template>
                </CatalogGrid>
                <Pagination
                    v-if="showPagination"
                    :pagination="pagination"
                    :catalogContext="catalogContext"
                    :class="paginationBottomPadding"
                    class="c-catalog-page__pagination o-flex-vertical-center o-flex-horizontal-center"
                />
                <slot></slot>
            </template>
        </CatalogGridContainer>
        <ContentfulContentBlock
            v-if="contentfulFooter"
            :contentRows="contentfulFooter"
            :contentTitle="contentfulTitle"
            :contentfulStartTime="contentfulStartTime"
            :contentfulEndTime="contentfulEndTime"
        />
        <slot name="custom-footer-content"></slot>
    </Layout>
</template>

<script setup>
import { ref, computed, markRaw, nextTick, watch, useSlots } from 'vue';
import { isEmpty } from 'lodash-es';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import { useState, useGetters, useActions, useMutations } from '~coreModules/core/js/composables/vuexHelpers';
import { useMediaQueries } from '~coreModules/core/js/composables/useMediaQueries';
import { useContentfulSvc } from '~coreModules/core/js/composables/useContentfulSvc';
import { useLogger } from '~coreModules/core/js/composables/useLogger';
import { useMounted } from '~coreModules/core/js/composables/useMounted';

import { GLOBAL_EVENT } from '~coreModules/core/js/store';
import { MODALS_MODULE_NAME } from '~coreModules/modals/js/modals-store';
import { getContentfulPageContentForSlug } from '~coreModules/contentful/js/contentful-client';
import { CONTENT_TYPES, CONTENT_SLUGS } from '~modules/contentful/js/contentful-constants';
import { shapeContentfulContentPageResponse } from '~coreModules/contentful/js/contentful-utils';
import { CATALOG_QUERY_SORT, CATALOG_QUERY_SLUG } from '~coreModules/catalog/js/catalog-constants';
import { VIEW_PRODUCT_LIST } from '~coreModules/core/js/global-event-constants';
import { getFilterStringFromObject, getPaginationOffset } from '~coreModules/catalog/js/catalog-utils';
import { CAROUSEL_IDS } from '~modules/analytics/js/analytics-constants';

import { PROFILE_MODULE_NAME } from '~modules/profile/js/profile-store';
import { BOX_MODULE_NAME } from '~modules/box/js/box-store';
import { TOGGLE_IN_BOX_STATUS } from '~modules/catalog/js/catalog-store';
import { getEditedChoiceIdInBox, getClearAllFiltersLinkObject } from '~modules/catalog/js/catalog-utils';
import { PRE_PURCHASE_MAX_PRODUCT_COUNT_DISPLAY } from '~modules/catalog/js/catalog-constants';
import { IMAGE_PRESETS } from '~modules/core/js/constants';
import { PRODUCT_TOUR_MODULE_NAME } from '~modules/product-tour/js/product-tour-store';
import { PRODUCT_TOUR_NAMES } from '~modules/product-tour/js/product-tour-constants';

import Layout from '~modules/core/components/layouts/Layout.vue';

import PageHeader from '~coreModules/core/components/ui/PageHeader.vue';
import CatalogResultsCount from '~coreModules/catalog/components/CatalogResultsCount.vue';
import CatalogGrid from '~coreModules/catalog/components/CatalogGrid.vue';
import CatalogGridContainer from '~coreModules/catalog/components/CatalogGridContainer.vue';
import CatalogEmptyFilteredContent from '~coreModules/catalog/components/CatalogEmptyFilteredContent.vue';

import ContentfulContentBlock from '~modules/contentful/components/ContentfulContentBlock.vue';
import CatalogActionBar from '~modules/catalog/components/CatalogActionBar.vue';
import CatalogTile from '~modules/catalog/components/CatalogTile.vue';
import CatalogInGridRow from '~modules/catalog/components/CatalogInGridRow.vue';
import CatalogInGridRowContainer from '~modules/catalog/components/CatalogInGridRowContainer.vue';
import Pagination from '~modules/core/components/ui/Pagination.vue';
import CurationSubNav from '~modules/catalog/components/CurationSubNav.vue';
import ProductTour from '~modules/product-tour/components/ProductTour.vue';

const props = defineProps({
    analyticsType: {
        type: String,
        default: CAROUSEL_IDS.CATALOG_IMAGE_VIEWER,
    },
    pageHeader: {
        type: Object,
        default: null,
    },
    contentfulTitle: {
        type: String,
        default: null,
    },
    contentfulStartTime: {
        type: String,
        default: null,
    },
    contentfulEndTime: {
        type: String,
        default: null,
    },
    contentfulTopper: {
        type: Array,
        default: null,
    },
    contentfulFooter: {
        type: Array,
        default: null,
    },
    contentfulInGridContentRows: {
        type: Array,
        default: null,
    },
    contentfulCurationNavItems: {
        type: Array,
        default: null,
    },
    contentfulFaceOutImage: {
        type: String,
        default: null,
    },
    enableFilterActionBar: {
        type: Boolean,
        default: true,
    },
    enableProductResultsCount: {
        type: Boolean,
        default: true,
    },
    products: {
        type: Array,
        required: true,
    },
    filterGroups: {
        type: Array,
        default: () => [],
    },
    catalogContext: {
        type: String,
        required: true,
    },
    isPrePurchase: {
        type: Boolean,
        default: false,
    },
    toggleProductClosetStatus: {
        type: Function,
        default: () => {},
    },
    pagination: {
        type: Object,
        required: true,
    },
    emptyCatalogContentfulContent: {
        type: Object,
        default: null,
    },
    sortOptions: {
        type: Array,
        required: true,
    },
    productListId: {
        type: String,
        default: '',
    },
    productListName: {
        type: String,
        default: '',
    },
    pageTitle: {
        type: String,
        default: '',
    },
    pageSubTitle: {
        type: String,
        default: '',
    },
    headerType: {
        type: String,
        default: '',
    },
    closetListId: {
        type: String,
        default: '',
    },
    pageHeaderClass: {
        type: String,
        default: '',
    },
    emptyFilteredContentSlug: {
        type: String,
        default: CONTENT_SLUGS.EMPTY_FILTERED_CATALOG_CONTENT_SLUG,
    },
    productTourData: {
        type: Object,
        default: () => ({}),
    },
    selectedCuration: {
        type: String,
        default: '',
    },
});

const { t } = useI18n();
const mediaQueries = useMediaQueries();
const route = useRoute();
const contentfulSvc = useContentfulSvc();
const logger = useLogger();
const slots = useSlots();
const isMounted = useMounted();

const { isSubscribed } = useGetters(PROFILE_MODULE_NAME, ['isSubscribed']);
const { boxIsOpen, choiceIdsInBox } = useGetters(BOX_MODULE_NAME, ['boxIsOpen', 'choiceIdsInBox']);
const { isOpen: modalIsOpen } = useGetters(MODALS_MODULE_NAME, ['isOpen']);
const { shouldRenderGivenTour } = useGetters(PRODUCT_TOUR_MODULE_NAME, ['shouldRenderGivenTour']);

/* eslint-disable vue/no-setup-props-reactivity-loss */
const {
    choiceIdsInCatalogAndBox,
    isCollectionPage,
} = useGetters(props.catalogContext, [
    'choiceIdsInCatalogAndBox',
    'isCollectionPage',
]);
const {
    appliedFilters,
    catalogResponseId,
    clientResponseUuid,
} = useState(props.catalogContext, {
    appliedFilters: state => state.catalog.appliedFilters,
    catalogResponseId: state => state.catalog.responseId,
    clientResponseUuid: state => state.catalog.clientResponseUuid,
});

const { toggleInBoxStatus } = useMutations(props.catalogContext, {
    toggleInBoxStatus: TOGGLE_IN_BOX_STATUS,
});
/* eslint-enable vue/no-setup-props-reactivity-loss */

const { trackGlobalEvent } = useActions({ trackGlobalEvent: GLOBAL_EVENT });

const filteredEmptyResultsContent = ref(null);

const contentfulContentBlockComponent = markRaw(ContentfulContentBlock);
const imagePreset = IMAGE_PRESETS.browse.regular;

const totalProductResults = computed(() => props.pagination.totalItems || 0);
const totalProductResultText = computed(() => {
    if (props.isPrePurchase && totalProductResults.value > PRE_PURCHASE_MAX_PRODUCT_COUNT_DISPLAY) {
        return `${PRE_PURCHASE_MAX_PRODUCT_COUNT_DISPLAY}+`;
    }

    return totalProductResults.value;
});
/* Selected filters display text for catalog filter button */
const selectedFilterOptionsText = computed(() => props.filterGroups
    .flatMap(filterGroup => filterGroup.filterValues
        .filter(filterValue => filterValue.isSelected)
        .flatMap(selectedFilter => selectedFilter.displayName))
    .join(', '));
/* Number of applied filters */
const selectedFilterOptionsCount = computed(() => (
    selectedFilterOptionsText.value ? selectedFilterOptionsText.value.split(',').length : 0
));
const showEmptyCatalogContent = computed(() => isEmpty(props.products) &&
        (!isEmpty(props.emptyCatalogContentfulContent) || props.pagination.totalItemsBeforeFilters > 0));
const showFilterEmptyResults = computed(() => (
    showEmptyCatalogContent.value && props.pagination.totalItemsBeforeFilters > 0
));
const showPageHeader = computed(() => props.pageHeader?.editorialText || props.headerType);
const productTourName = computed(() => Object.keys(props.productTourData).find(tour => tour) ||
        PRODUCT_TOUR_NAMES.catalog);
const shouldShowProductTour = computed(() => !modalIsOpen.value &&
    !isCollectionPage.value &&
    shouldRenderGivenTour(productTourName.value) &&
    props.enableFilterActionBar);
const defaultPageHeaderClass = computed(() => (props.enableFilterActionBar ? 'u-spacer--0pt5' : 'u-spacer--2'));
const showPagination = computed(() => props.pagination.totalPages > 1);
const gridBottomPadding = computed(() => (!showPagination.value && (props.contentfulFooter ||
    (!props.contentfulFooter && !!slots['custom-footer-content'])) ?
    'c-catalog-page__grid--last-el' : ''));
const paginationBottomPadding = computed(() => (props.contentfulFooter ? 'c-catalog-page__pagination--last-el' : ''));
const displayTilesAsCarousel = computed(() => !mediaQueries.isSmallish);
const categorySlug = computed(() => route.params[CATALOG_QUERY_SLUG] || '');
const sortMethod = computed(() => appliedFilters.value[CATALOG_QUERY_SORT] || props.sortOptions?.[0]?.slug);
const showBoxCTAs = computed(() => {
    if (props.isPrePurchase) return false;

    return isSubscribed.value && boxIsOpen.value;
});
const queryFilters = computed(() => {
    const curation = props.selectedCuration;
    return {
        ...(curation && {
            curation,
        }),
        ...appliedFilters.value,
    };
});

function isTileValidForProductTour(index) {
    return index === 0 && shouldShowProductTour.value;
}

watch(clientResponseUuid, async () => {
    await nextTick();

    const { itemsPerPage, pageNumber, totalItems } = props.pagination;
    const faceOut = props.contentfulFaceOutImage?.toLowerCase();

    trackGlobalEvent({
        type: VIEW_PRODUCT_LIST,
        data: {
            product: {
                productListId: props.productListId || categorySlug.value,
                productListName: props.productListName || props.catalogContext,
            },
            productList: {
                sortMethod: sortMethod.value,
                slug: categorySlug.value,
                responseId: catalogResponseId.value,
                offset: getPaginationOffset(pageNumber, itemsPerPage),
                filterString: getFilterStringFromObject(queryFilters.value),
                numberOfResults: totalItems,
            },
            curation: props.selectedCuration,
            faceOut,
        },
    });
}, { immediate: true });

watch(choiceIdsInBox, (newBoxChoiceIds, oldBoxChoiceIds) => {
    const editedChoiceId = getEditedChoiceIdInBox(
        newBoxChoiceIds,
        oldBoxChoiceIds,
        choiceIdsInCatalogAndBox.value,
    );

    if (editedChoiceId) {
        toggleInBoxStatus(editedChoiceId);
    }
});

watch(showEmptyCatalogContent, async () => {
    if (showFilterEmptyResults.value && !filteredEmptyResultsContent.value) {
        try {
            const response = await getContentfulPageContentForSlug({
                contentfulSvc,
                contentType: CONTENT_TYPES.CONTENT_SLOT,
                slug: props.emptyFilteredContentSlug,
                isTimed: false,
            });
            filteredEmptyResultsContent.value = shapeContentfulContentPageResponse(response).rows;
        } catch (error) {
            logger.debugError('Contentful content not found for slug no-filter-results: ', error);
        }

    }
}, { immediate: true });
</script>

<style lang="scss">
.c-catalog-page {
    &__grid {
        grid-column-gap: $nu-spacer-2;
        row-gap: $nu-spacer-2;
        padding-bottom: $nu-spacer-6;
        grid-auto-rows: 1fr;

        @include breakpoint(small) {
            grid-column-gap: $nu-spacer-3;
            row-gap: $nu-spacer-3;
        }

        &--last-el {
            padding-bottom: $nu-spacer-8;

            @include breakpoint(medium) {
                padding-bottom: $nu-spacer-10;
            }
        }
    }

    &__pagination {
        padding-bottom: $nu-spacer-8;

        &--last-el {
            padding-bottom: $nu-spacer-10;

            @include breakpoint(medium) {
                padding-bottom: $nu-spacer-12;
            }
        }
    }
}
</style>
